import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useGlobalSearch } from '@/hooks/useGlobalSearch';
import { GlobalSearchResults } from './GlobalSearchResults';

/**
 * Composant de recherche globale dans la navigation
 * Permet de rechercher à travers toutes les entités du CRM
 */

export function NavigationSearch() {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const searchState = useGlobalSearch(query);

  // Gérer le raccourci clavier Cmd+K / Ctrl+K
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }

      // Fermer avec Échap
      if (e.key === 'Escape') {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Fermer le popover quand on clique à l'extérieur
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(value.length > 0);
  };

  const handleInputFocus = () => {
    if (query.length > 0) {
      setIsOpen(true);
    }
  };

  const handleResultClick = () => {
    setQuery('');
    setIsOpen(false);
    inputRef.current?.blur();
  };

  // Détecter si on est sur Mac pour afficher le bon raccourci
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPod|iPad/.test(navigator.platform);
  const shortcutKey = isMac ? '⌘K' : 'Ctrl+K';

  return (
    <div className="w-full relative" ref={searchRef}>
      <div className="relative">
        <Search
          className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500"
          aria-hidden="true"
        />
        <Input
          ref={inputRef}
          type="search"
          placeholder="Rechercher"
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          className="pl-9 pr-16 h-9 bg-white border-zinc-200 shadow-sm"
          aria-label="Rechercher"
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1 text-xs text-zinc-400 pointer-events-none">
          <span>{shortcutKey}</span>
        </div>
      </div>

      {/* Afficher les résultats */}
      {isOpen && (
        <GlobalSearchResults
          searchState={searchState}
          query={query}
          onResultClick={handleResultClick}
        />
      )}
    </div>
  );
}
